import React, { useState } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const emailTemplateSeeds = [
  {
    template_key: 'enrollment_confirmation',
    name: 'Enrollment Confirmation',
    subject: 'âœ… Enrollment Confirmed: {{productName}}',
    category: 'enrollment',
    variables: ['employeeName', 'productName', 'coverageLevel', 'monthlyPremium', 'coverageAmount', 'effectiveDate', 'taxSavings', 'dashboardUrl', 'employerName'],
    body_html: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #334155; margin: 0; padding: 0; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #10b981 0%, #14b8a6 100%); color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: white; padding: 30px 20px; border: 1px solid #e2e8f0; }
    .footer { background: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; border-radius: 0 0 8px 8px; }
    .button { display: inline-block; background: #10b981; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; }
    .info-box { background: #f0fdf4; border-left: 4px solid #10b981; padding: 15px; margin: 20px 0; }
    .table { width: 100%; border-collapse: collapse; margin: 10px 0; }
    .table td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
    .table td:first-child { font-weight: 600; color: #475569; width: 40%; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0;">Enrollment Confirmed! ðŸŽ‰</h1>
      <p style="margin: 10px 0 0 0; opacity: 0.9;">Your Section 125 benefit is now active</p>
    </div>
    <div class="content">
      <p>Hi {{employeeName}},</p>
      <p>Great news! Your enrollment in <strong>{{productName}}</strong> has been successfully processed.</p>
      
      <div class="info-box">
        <h3 style="margin-top: 0;">Enrollment Details</h3>
        <table class="table">
          <tr><td>Product:</td><td>{{productName}}</td></tr>
          <tr><td>Coverage Level:</td><td>{{coverageLevel}}</td></tr>
          <tr><td>Monthly Premium:</td><td>$\{{monthlyPremium}}</td></tr>
          <tr><td>Coverage Amount:</td><td>$\{{coverageAmount}}</td></tr>
          <tr><td>Effective Date:</td><td>{{effectiveDate}}</td></tr>
        </table>
      </div>

      <div class="info-box">
        <h3 style="margin-top: 0;">ðŸ’° Your Tax Savings</h3>
        <p style="font-size: 18px; font-weight: bold; color: #10b981; margin: 10px 0;">
          Estimated Annual Tax Savings: $\{{taxSavings}}
        </p>
      </div>

      <p>Your premium deductions will begin with your next payroll cycle.</p>
      
      <div style="text-align: center; margin: 30px 0;">
        <a href="{{dashboardUrl}}" class="button">View My Benefits Dashboard</a>
      </div>

      <p>Best regards,<br><strong>{{employerName}}</strong></p>
    </div>
    <div class="footer">
      <p>&copy; ${new Date().getFullYear()} {{employerName}}. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`,
    is_active: true
  },
  {
    template_key: 'flex_proposal',
    name: 'Section 125 Premium Proposal',
    subject: 'ðŸ’° Your Personalized Section 125 Tax Savings Proposal',
    category: 'enrollment',
    variables: ['employeeName', 'optimalFlex', 'annualSavings', 'monthlyBenefit', 'enrollmentUrl', 'employerName'],
    body_html: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #334155; margin: 0; padding: 0; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #10b981 0%, #14b8a6 100%); color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: white; padding: 30px 20px; border: 1px solid #e2e8f0; }
    .footer { background: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; border-radius: 0 0 8px 8px; }
    .button { display: inline-block; background: #10b981; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; }
    .info-box { background: #f0fdf4; border-left: 4px solid #10b981; padding: 15px; margin: 20px 0; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0;">Your Personalized Section 125 Proposal ðŸ“Š</h1>
      <p style="margin: 10px 0 0 0; opacity: 0.9;">Maximize your tax savings</p>
    </div>
    <div class="content">
      <p>Hi {{employeeName}},</p>
      <p>We've calculated the optimal Section 125 premium amount for you based on your income and tax situation.</p>
      
      <div class="info-box">
        <h2 style="margin-top: 0; color: #10b981;">Recommended: $\{{optimalFlex}}/month</h2>
        <p style="font-size: 16px; margin: 10px 0;">
          <strong>Annual Tax Savings: $\{{annualSavings}}</strong>
        </p>
        <p style="color: #64748b;">Monthly Benefit: $\{{monthlyBenefit}}</p>
      </div>

      <h3>How It Works</h3>
      <ul>
        <li>Pre-tax dollars reduce your taxable income</li>
        <li>Lower taxable income = less federal, state, and FICA taxes</li>
        <li>Use premium dollars for insurance premiums</li>
        <li>Save up to $\{{annualSavings}} per year</li>
      </ul>

      <div style="text-align: center; margin: 30px 0;">
        <a href="{{enrollmentUrl}}" class="button">Start Enrollment</a>
      </div>
    </div>
    <div class="footer">
      <p>&copy; ${new Date().getFullYear()} {{employerName}}. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`,
    is_active: true
  },
  {
    template_key: 'employee_invitation',
    name: 'Employee Welcome & Invitation',
    subject: 'ðŸŽ‰ Welcome to Your Benefits Program',
    category: 'enrollment',
    variables: ['employeeName', 'employerName', 'enrollmentUrl', 'enrollmentDeadline', 'hrEmail'],
    body_html: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #334155; margin: 0; padding: 0; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #10b981 0%, #14b8a6 100%); color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: white; padding: 30px 20px; border: 1px solid #e2e8f0; }
    .footer { background: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; border-radius: 0 0 8px 8px; }
    .button { display: inline-block; background: #10b981; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; }
    .info-box { background: #f0fdf4; border-left: 4px solid #10b981; padding: 15px; margin: 20px 0; }
    .warning-box { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 20px 0; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0;">Welcome to Your Benefits! ðŸŽ‰</h1>
      <p style="margin: 10px 0 0 0; opacity: 0.9;">{{employerName}}</p>
    </div>
    <div class="content">
      <p>Hi {{employeeName}},</p>
      <p>You've been enrolled in the Section 125 benefits program! This program allows you to save on taxes while protecting yourself and your family.</p>
      
      <div class="info-box">
        <h3 style="margin-top: 0;">Next Steps</h3>
        <ol style="margin: 0; padding-left: 20px;">
          <li>Complete your employee profile</li>
          <li>Review available benefits</li>
          <li>Calculate your optimal premium amount</li>
          <li>Enroll in your chosen benefits</li>
          <li>Sign your enrollment forms</li>
        </ol>
      </div>

      <div class="warning-box">
        <p style="margin: 0;"><strong>â° Enrollment Deadline:</strong> {{enrollmentDeadline}}</p>
      </div>

      <div style="text-align: center; margin: 30px 0;">
        <a href="{{enrollmentUrl}}" class="button">Start Enrollment</a>
      </div>

      <p>Need help? Contact us at {{hrEmail}}</p>
    </div>
    <div class="footer">
      <p>&copy; ${new Date().getFullYear()} {{employerName}}. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`,
    is_active: true
  },
  {
    template_key: 'qle_approved',
    name: 'Qualifying Life Event Approved',
    subject: 'âœ… Qualifying Life Event Approved',
    category: 'qle',
    variables: ['employeeName', 'eventType', 'eventDate', 'enrollmentStart', 'enrollmentEnd', 'enrollmentUrl', 'employerName'],
    body_html: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #334155; margin: 0; padding: 0; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #10b981 0%, #14b8a6 100%); color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: white; padding: 30px 20px; border: 1px solid #e2e8f0; }
    .footer { background: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; border-radius: 0 0 8px 8px; }
    .button { display: inline-block; background: #10b981; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; }
    .info-box { background: #f0fdf4; border-left: 4px solid #10b981; padding: 15px; margin: 20px 0; }
    .table { width: 100%; border-collapse: collapse; margin: 10px 0; }
    .table td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
    .table td:first-child { font-weight: 600; color: #475569; width: 40%; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0;">Qualifying Life Event Approved âœ…</h1>
    </div>
    <div class="content">
      <p>Hi {{employeeName}},</p>
      <p>Your qualifying life event request has been approved!</p>
      
      <div class="info-box">
        <h3 style="margin-top: 0;">Event Details</h3>
        <table class="table">
          <tr><td>Event Type:</td><td>{{eventType}}</td></tr>
          <tr><td>Event Date:</td><td>{{eventDate}}</td></tr>
          <tr><td>Special Enrollment Window:</td><td>{{enrollmentStart}} to {{enrollmentEnd}}</td></tr>
        </table>
      </div>

      <p>You now have until <strong>{{enrollmentEnd}}</strong> to make changes to your benefits.</p>
      
      <div style="text-align: center; margin: 30px 0;">
        <a href="{{enrollmentUrl}}" class="button">Update My Benefits</a>
      </div>
    </div>
    <div class="footer">
      <p>Your special enrollment window is time-sensitive. Please complete changes before the deadline.</p>
    </div>
  </div>
</body>
</html>`,
    is_active: true
  },
  {
    template_key: 'claim_approved',
    name: 'Claim Approved',
    subject: 'ðŸ’š Claim Approved - Payment Processing',
    category: 'claim',
    variables: ['employeeName', 'claimAmount', 'claimType', 'claimDate', 'approvedDate', 'employerName'],
    body_html: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #334155; margin: 0; padding: 0; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #10b981 0%, #14b8a6 100%); color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: white; padding: 30px 20px; border: 1px solid #e2e8f0; }
    .footer { background: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; border-radius: 0 0 8px 8px; }
    .info-box { background: #f0fdf4; border-left: 4px solid #10b981; padding: 15px; margin: 20px 0; }
    .table { width: 100%; border-collapse: collapse; margin: 10px 0; }
    .table td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
    .table td:first-child { font-weight: 600; color: #475569; width: 40%; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0;">Claim Approved ðŸ’š</h1>
    </div>
    <div class="content">
      <p>Hi {{employeeName}},</p>
      <p>Good news! Your claim has been approved for payment.</p>
      
      <div class="info-box">
        <h3 style="margin-top: 0;">Claim Details</h3>
        <table class="table">
          <tr><td>Claim Amount:</td><td>$\{{claimAmount}}</td></tr>
          <tr><td>Claim Type:</td><td>{{claimType}}</td></tr>
          <tr><td>Claim Date:</td><td>{{claimDate}}</td></tr>
          <tr><td>Approved Date:</td><td>{{approvedDate}}</td></tr>
        </table>
      </div>

      <p>Payment will be processed within 5-7 business days.</p>
    </div>
    <div class="footer">
      <p>Thank you for being part of our Section 125 benefits program.</p>
      <p>&copy; ${new Date().getFullYear()} {{employerName}}. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`,
    is_active: true
  },
  {
    template_key: 'enrollment_reminder',
    name: 'Enrollment Deadline Reminder',
    subject: 'â° Reminder: Benefits Enrollment Deadline Approaching',
    category: 'reminder',
    variables: ['employeeName', 'daysRemaining', 'enrollmentDeadline', 'enrollmentUrl', 'employerName'],
    body_html: `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #334155; margin: 0; padding: 0; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%); color: white; padding: 30px 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .content { background: white; padding: 30px 20px; border: 1px solid #e2e8f0; }
    .footer { background: #f8fafc; padding: 20px; text-align: center; font-size: 12px; color: #64748b; border-radius: 0 0 8px 8px; }
    .button { display: inline-block; background: #f59e0b; color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; font-weight: bold; }
    .warning-box { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 20px 0; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0;">â° Enrollment Deadline Approaching</h1>
    </div>
    <div class="content">
      <p>Hi {{employeeName}},</p>
      <p>This is a friendly reminder that your benefits enrollment deadline is approaching.</p>
      
      <div class="warning-box">
        <h3 style="margin-top: 0; color: #f59e0b;">Time Remaining: {{daysRemaining}} days</h3>
        <p style="margin: 5px 0 0 0; font-size: 16px;">
          <strong>Deadline: {{enrollmentDeadline}}</strong>
        </p>
      </div>

      <p>Don't miss out on valuable benefits and tax savings! Complete your enrollment today.</p>
      
      <div style="text-align: center; margin: 30px 0;">
        <a href="{{enrollmentUrl}}" class="button">Complete Enrollment Now</a>
      </div>
    </div>
    <div class="footer">
      <p>&copy; ${new Date().getFullYear()} {{employerName}}. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`,
    is_active: true
  }
];

export default function EmailTemplateSeedData({ onComplete }) {
  const [loading, setLoading] = useState(false);
  const [seeded, setSeeded] = useState(false);

  const seedTemplates = async () => {
    setLoading(true);
    try {
      const result = await api.entities.EmailTemplate.bulkCreate(emailTemplateSeeds);
      setSeeded(true);
      const count = result.count || emailTemplateSeeds.length;
      if (count === 0) {
        toast.info('All email templates already exist in the database');
      } else {
        toast.success(`Successfully seeded ${count} email template${count !== 1 ? 's' : ''}`);
      }
      if (onComplete) onComplete();
    } catch (err) {
      console.error('Seed error:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to seed email templates');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-700 dark:text-blue-400" />
          Seed Email Templates
        </CardTitle>
        <CardDescription>
          Load standard email templates for enrollment, claims, and notifications
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Includes templates for enrollment confirmations, premium proposals, QLE approvals, claim notifications, and reminders.
        </p>

        {seeded ? (
          <div className="flex items-center gap-2 text-emerald-600">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">Email templates seeded successfully!</span>
          </div>
        ) : (
          <Button
            onClick={seedTemplates}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Seeding...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Seed Email Templates
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
